import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/components/ui/use-toast";
import { AdminCatalogProduct } from "../api/fetchCatalogProducts";
import { updateCatalogProduct } from "../api/updateCatalogProduct";
import { deleteCatalogProduct } from "../api/deleteCatalogProduct";
import { SaveCatalogProductRequest } from "../api/createCatalogProduct";
import { createCatalogProductColumns } from "../components/CatalogProductColumns/CatalogProductColumns";
import { useCatalogProducts } from "./useCatalogProducts";

function toSaveRequest(
  product: AdminCatalogProduct,
): SaveCatalogProductRequest {
  return {
    slug: product.slug,
    name: product.name,
    tagline: product.tagline,
    badge: product.badge,
    categoryId: product.category.id,
    audiences: product.audiences,
    trades: product.trades,
    stageUuids: product.stages.map((stage) => stage.id),
    thumbnailFileUuid: product.thumbnailFileUuid,
    featured: product.featured,
    featuredOrder: product.featuredOrder,
    landingPageKey: product.landingPageKey,
    sortOrder: product.sortOrder,
    isPublished: product.isPublished,
  };
}

export function useCatalogProductList() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [refreshCounter, setRefreshCounter] = useState(0);
  const productsQuery = useCatalogProducts(refreshCounter);

  const [deleteDialogState, setDeleteDialogState] = useState({
    isOpen: false,
    productId: null as string | null,
    productName: "",
  });

  const refreshList = useCallback(() => {
    setRefreshCounter((prev) => prev + 1);
  }, []);

  const handleEditProduct = useCallback(
    (product: AdminCatalogProduct) => {
      navigate(`/admin/product-catalog/products/${product.id}/edit`);
    },
    [navigate],
  );

  const handleTogglePublished = useCallback(
    async (product: AdminCatalogProduct) => {
      await updateCatalogProduct(product.id, {
        ...toSaveRequest(product),
        isPublished: !product.isPublished,
      }).finally(refreshList);
    },
    [refreshList],
  );

  const handleToggleFeatured = useCallback(
    async (product: AdminCatalogProduct) => {
      await updateCatalogProduct(product.id, {
        ...toSaveRequest(product),
        featured: !product.featured,
        featuredOrder: product.featured ? null : product.featuredOrder,
      }).finally(refreshList);
    },
    [refreshList],
  );

  const handleDeleteProduct = useCallback((product: AdminCatalogProduct) => {
    setDeleteDialogState({
      isOpen: true,
      productId: product.id,
      productName: product.name,
    });
  }, []);

  const closeDeleteDialog = useCallback(() => {
    setDeleteDialogState({ isOpen: false, productId: null, productName: "" });
  }, []);

  const [isDeleting, setIsDeleting] = useState(false);

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteDialogState.productId || isDeleting) return;

    setIsDeleting(true);
    try {
      await deleteCatalogProduct(deleteDialogState.productId);
      closeDeleteDialog();
      refreshList();
      toast({
        title: "Success",
        description: "Catalog product deleted",
        variant: "default",
      });
    } catch {
      // Global interceptor already toasted the failure; keep the dialog
      // open so the admin sees the delete did not happen.
    } finally {
      setIsDeleting(false);
    }
  }, [
    deleteDialogState.productId,
    isDeleting,
    closeDeleteDialog,
    refreshList,
    toast,
  ]);

  const goToCreateProduct = useCallback(() => {
    navigate("/admin/product-catalog/products/new");
  }, [navigate]);

  const columns = useMemo(
    () =>
      createCatalogProductColumns(
        handleEditProduct,
        handleDeleteProduct,
        handleTogglePublished,
        handleToggleFeatured,
      ),
    [
      handleEditProduct,
      handleDeleteProduct,
      handleTogglePublished,
      handleToggleFeatured,
    ],
  );

  return {
    ...productsQuery,
    columns,
    deleteDialogState,
    closeDeleteDialog,
    handleConfirmDelete,
    isDeleting,
    goToCreateProduct,
  };
}
